/**
 * VERA Authentication Provider
 * Simple username/password authentication using environment-configured credentials.
 * Auth state persisted in sessionStorage for tab-level sessions.
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import {
  makeStyles,
  Text,
  Input,
  Button,
  Spinner,
} from "@fluentui/react-components";
import {
  LockClosed24Regular,
  Person24Regular,
  Eye24Regular,
  EyeOff24Regular,
  Shield24Filled,
} from "@fluentui/react-icons";
import type { AuthState } from "../types";
import { onLogin as auditLogin, onLogout as auditLogout, resumeSession as auditResume } from "../services/auditService";

// ── Auth Context ──────────────────────────────────────────────

interface AuthContextValue extends AuthState {
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  username: null,
  displayName: null,
  login: async () => false,
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

// ── Styles ────────────────────────────────────────────────────

const useLoginStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    gap: "24px",
    padding: "20px",
    background:
      "linear-gradient(135deg, #004C97 0%, #003a75 50%, #2D2926 100%)",
  },
  logoCard: {
    backgroundColor: "#ffffff",
    padding: "24px 32px",
    borderRadius: "12px",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
  },
  logoImage: {
    height: "120px",
    width: "auto",
  },
  brandName: {
    color: "#ffffff",
    fontFamily: "'Poppins', sans-serif",
    marginTop: "8px",
    fontSize: "28px",
    fontWeight: 600,
    letterSpacing: "3px",
  },
  brandSubtext: {
    color: "rgba(255, 255, 255, 0.7)",
    fontFamily: "'Open Sans', sans-serif",
    marginTop: "-8px",
    fontSize: "14px",
    letterSpacing: "0.5px",
  },
  loginCard: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "40px",
    width: "100%",
    maxWidth: "420px",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  loginHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "8px",
  },
  loginIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #004C97 0%, #0066cc 100%)",
    color: "#ffffff",
  },
  fieldLabel: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#2D2926",
    fontFamily: "'Poppins', sans-serif",
    marginBottom: "4px",
  },
  inputWrapper: {
    position: "relative" as const,
  },
  eyeButton: {
    position: "absolute" as const,
    right: "8px",
    top: "50%",
    transform: "translateY(-50%)",
    minWidth: "32px",
    height: "32px",
  },
  errorText: {
    color: "#c4314b",
    fontSize: "13px",
    textAlign: "center" as const,
  },
  loginButton: {
    marginTop: "8px",
    height: "44px",
    fontSize: "15px",
    fontWeight: 600,
    fontFamily: "'Poppins', sans-serif",
  },
  footer: {
    color: "rgba(255, 255, 255, 0.5)",
    fontFamily: "'Open Sans', sans-serif",
    marginTop: "12px",
    fontSize: "12px",
  },
});

// ── Login Page ────────────────────────────────────────────────

const LoginPage: React.FC<{
  onLogin: (username: string, password: string) => Promise<boolean>;
}> = ({ onLogin }) => {
  const styles = useLoginStyles();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const success = await onLogin(username, password);
      if (!success) {
        setError("Invalid credentials. Please try again.");
      }
    } catch {
      setError("Authentication failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.root}>
      {/* LensLock Logo */}
      <div className={styles.logoCard}>
        <img
          src="/lenslock_logo_color_tagline.png"
          alt="LensLock - We Serve Heroes Every Day"
          className={styles.logoImage}
        />
      </div>

      {/* VERA Branding */}
      <Text className={styles.brandName}>A R R O Y O 3 6 0</Text>
      <Text className={styles.brandSubtext}>
        Virtual Evidence & Reconstruction Archive by LensLock
      </Text>

      {/* Login Form */}
      <form onSubmit={handleSubmit} className={styles.loginCard}>
        <div className={styles.loginHeader}>
          <div className={styles.loginIcon}>
            <Shield24Filled />
          </div>
          <div>
            <Text weight="semibold" size={500} style={{ fontFamily: "'Poppins', sans-serif" }}>
              Secure Login
            </Text>
            <Text size={200} style={{ color: "#63666A", display: "block" }}>
              Enter your credentials to access the evidence room
            </Text>
          </div>
        </div>

        <div>
          <Text className={styles.fieldLabel}>Username</Text>
          <Input
            size="large"
            contentBefore={<Person24Regular />}
            placeholder="Enter username"
            value={username}
            onChange={(_, data) => setUsername(data.value)}
            autoComplete="username"
            autoFocus
          />
        </div>

        <div>
          <Text className={styles.fieldLabel}>Password</Text>
          <div className={styles.inputWrapper}>
            <Input
              size="large"
              contentBefore={<LockClosed24Regular />}
              contentAfter={
                <Button
                  appearance="transparent"
                  size="small"
                  icon={showPassword ? <EyeOff24Regular /> : <Eye24Regular />}
                  onClick={() => setShowPassword(!showPassword)}
                  className={styles.eyeButton}
                />
              }
              type={showPassword ? "text" : "password"}
              placeholder="Enter password"
              value={password}
              onChange={(_, data) => setPassword(data.value)}
              autoComplete="current-password"
            />
          </div>
        </div>

        {error && <Text className={styles.errorText}>{error}</Text>}

        <Button
          appearance="primary"
          type="submit"
          className={styles.loginButton}
          disabled={loading || !username || !password}
          icon={loading ? <Spinner size="tiny" /> : undefined}
        >
          {loading ? "Signing in..." : "Sign In"}
        </Button>
      </form>

      <Text className={styles.footer}>
        Arroyo360 Evidence Portal • LensLock Confidential • Authorized Access Only
      </Text>
    </div>
  );
};

// ── Auth Provider ─────────────────────────────────────────────

const SESSION_KEY = "vera_auth";

interface StoredAuth {
  username: string;
  displayName: string;
}

function getStoredAuth(): StoredAuth | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) return JSON.parse(raw) as StoredAuth;
  } catch {
    // ignore
  }
  return null;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const stored = getStoredAuth();
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: !!stored,
    username: stored?.username ?? null,
    displayName: stored?.displayName ?? null,
  });

  const login = useCallback(
    async (username: string, password: string): Promise<boolean> => {
      // Validate against environment-configured credentials
      const validUser =
        process.env.REACT_APP_AUTH_USERNAME || "vera-admin";
      const validPass =
        process.env.REACT_APP_AUTH_PASSWORD || "LensLock2024!";
      const displayName =
        process.env.REACT_APP_AUTH_DISPLAY_NAME || "Arroyo360 Administrator";

      if (username === validUser && password === validPass) {
        const auth: StoredAuth = { username, displayName };
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(auth));
        setAuthState({
          isAuthenticated: true,
          username,
          displayName,
        });

        // Record login event for audit trail (fire-and-forget)
        auditLogin(username).catch(() => {});

        return true;
      }
      return false;
    },
    []
  );

  const logout = useCallback(() => {
    // Record logout event for audit trail (fire-and-forget)
    auditLogout().catch(() => {});

    sessionStorage.removeItem(SESSION_KEY);
    setAuthState({
      isAuthenticated: false,
      username: null,
      displayName: null,
    });
  }, []);

  // Resume audit heartbeat for existing sessions (e.g. page refresh)
  useEffect(() => {
    if (authState.isAuthenticated) {
      auditResume();
    }
  }, [authState.isAuthenticated]);

  if (!authState.isAuthenticated) {
    return <LoginPage onLogin={login} />;
  }

  return (
    <AuthContext.Provider value={{ ...authState, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
